import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import { useState } from "react";
import { HiOutlinePencilAlt, HiTrash } from "react-icons/hi";
import { toast } from "react-toastify";
import type {
  Exercise,
  ExerciseForm,
  Equipment,
  Muscle,
} from "types/apiResponses";
import { updateExercise } from "../../services/exercises";
import DeleteExerciseModal from "./DeleteExerciseModal";
import ExerciseModal from "./ExerciseModal";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

export interface AllExercisesTableProps {
  exercises: Exercise[];
  getExercises: () => void;
  loading: boolean;
}

const AllExercisesTable = function (props: AllExercisesTableProps) {
  const [updating, setUpdating] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [exerciseFormData, setExerciseFormData] = useState<ExerciseForm>({
    id: "",
    name: "",
    description: "",
    repCount: "",
    setCount: "",
    exerciseType: "",
    equipment: [],
    muscles: [],
    muscleGroups: [],
    imageUrl: "",
  });
  const queryClient = useQueryClient();

  const handleSubmitUpdate = async (submittedData: Partial<ExerciseForm>) => {
    setUpdating(true);

    try {
      const idToUpdate = exerciseFormData.id;

      if (!idToUpdate) {
        toast.error("Exercise ID is missing, cannot update.");
        Sentry.captureMessage("Attempted to update exercise without ID");
        setUpdating(false);
        return;
      }

      console.log(
        "Updating exercise with ID:",
        idToUpdate,
        "and data:",
        submittedData,
      );
      if (submittedData.equipment) {
        console.log(
          "Equipment being sent for update:",
          submittedData.equipment,
        );
      }

      const updateResponse = await updateExercise(submittedData, idToUpdate);

      if (updateResponse.data.error) {
        toast.error(updateResponse.data.message);
        Sentry.captureMessage(updateResponse.data.message);
      } else {
        queryClient.invalidateQueries({
          queryKey: ["exerciseDetails", idToUpdate],
        });
        props.getExercises();
        toast.success("Exercise updated successfully!");
      }
    } catch (error) {
      toast.error("Failed to update exercise");
      Sentry.captureException(error);
    } finally {
      setUpdating(false);
    }
  };

  const handleEditExercise = (exercise: Exercise) => {
    setExerciseFormData({
      id: exercise.id,
      name: exercise.name,
      description: exercise.description || "",
      repCount: exercise.repCount || "",
      setCount: exercise.setCount || "",
      exerciseType: exercise.exerciseType.id,
      equipment: exercise.equipment?.map((eq: Equipment) => eq.id) || [],
      muscles: exercise.muscles.map((muscle: Muscle) => muscle.id),
      muscleGroups: exercise.muscleGroups?.map((mg: any) => mg.id) || [],
      imageUrl: exercise.imageUrl || "",
    });
    setOpen(true);
  };

  const handleDeleteExercise = (exercise: Exercise) => {
    setExerciseFormData({
      id: exercise.id,
      name: exercise.name,
      exerciseType: exercise.exerciseType.id,
      equipment: exercise.equipment?.map((eq: any) => eq.id) || [],
      muscles: exercise.muscles.map((x: any) => x.id),
      muscleGroups: exercise.muscleGroups?.map((mg: any) => mg.id) || [],
    });
    setDeleteModalOpen(true);
  };

  return (
    <>
      <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
        <TableHead className="bg-gray-100 dark:bg-gray-700">
          <TableRow>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell className="hidden sm:table-cell">Type</TableHeadCell>
            <TableHeadCell className="hidden lg:table-cell">
              Muscles
            </TableHeadCell>
            <TableHeadCell className="hidden md:table-cell">Sets</TableHeadCell>
            <TableHeadCell className="hidden xl:table-cell">
              Equipment
            </TableHeadCell>
            <TableHeadCell>Actions</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
          {props.exercises?.map((exercise: Exercise, index: number) => {
            return (
              <TableRow
                key={index}
                className="hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <TableCell className="whitespace-nowrap px-6 py-4">
                  <div className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    <div className="text-base font-semibold text-gray-900 dark:text-white">
                      {exercise.name}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden whitespace-nowrap text-base font-medium text-gray-900 dark:text-white sm:table-cell">
                  {exercise.exerciseType.name}
                </TableCell>
                <TableCell className="hidden whitespace-nowrap text-base font-medium text-gray-900 dark:text-white lg:table-cell">
                  {exercise.muscles?.map((muscle: any) => {
                    return (
                      <span key={muscle.name} className="p-2 text-xs">
                        {muscle.name}
                      </span>
                    );
                  })}
                </TableCell>
                <TableCell className="hidden whitespace-nowrap text-base font-medium text-gray-900 dark:text-white md:table-cell">
                  {exercise.setCount || "N/A"}
                </TableCell>
                <TableCell className="hidden whitespace-nowrap text-base font-medium text-gray-900 dark:text-white xl:table-cell">
                  {Array.isArray(exercise.equipment) &&
                    exercise.equipment.map((eq: any) => {
                      return (
                        <span key={eq.name} className="p-2 text-xs">
                          {eq.name}
                        </span>
                      );
                    })}
                  {(!exercise.equipment || exercise.equipment.length === 0) && (
                    <span className="p-2 text-xs text-gray-500 dark:text-gray-400">
                      N/A
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap px-6 py-4">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handleEditExercise(exercise)}
                    >
                      <div className="flex items-center gap-x-2">
                        <HiOutlinePencilAlt className="text-lg" />
                        <span className="hidden sm:inline">Edit Exercise</span>
                        <span className="sm:hidden">Edit</span>
                      </div>
                    </Button>
                    <Button
                      color="failure"
                      size="sm"
                      onClick={() => handleDeleteExercise(exercise)}
                    >
                      <div className="flex items-center gap-x-2">
                        <HiTrash className="text-lg" />
                        <span className="hidden sm:inline">Delete</span>
                      </div>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {isOpen && (
        <ExerciseModal
          id={exerciseFormData.id}
          updating={updating}
          isOpen={isOpen}
          exercise={exerciseFormData}
          handleSubmit={
            handleSubmitUpdate as (
              data: ExerciseForm | Partial<ExerciseForm>,
            ) => Promise<void>
          }
          handleClose={() => setOpen(false)}
        />
      )}
      {deleteModalOpen && (
        <DeleteExerciseModal
          exercise={exerciseFormData}
          getExercises={props.getExercises}
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
        />
      )}
    </>
  );
};

export default AllExercisesTable;
